import { useCallback } from 'react';
import { cloneDeep as clonedeep } from 'lodash-es';

import { useFsuiContext } from '../../../../financial-services-ui/fsui-context-provider';
import { FORM_REQUIREMENTS } from '../../../utilities/constants';
import { useWalletRegistrationFormContext } from '../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../contexts/wallet-registration-modal-context';

/**
 * @typedef {object} SubmissionResult
 * @property {object} submissionData - The prepared submission data
 * @property {string[]} allLabelProviderIds - All label provider IDs including non-default
 * @property {string[]} nonDefaultResourceRequested - Non-default resource IDs that were requested
 */

/**
 * Hook that prepares wallet registration submission data.
 * Extracts common logic used by FundLaterButton, AddFundsButton, and SubmitButton.
 * @returns {{ prepareSubmissionData: () => SubmissionResult }}
 */
export const usePrepareSubmissionData = () => {
  const { walletForm } = useWalletRegistrationFormContext();
  const { shouldShowAdyenDropInComponent, nonDefaultTerms } = useWalletRegistrationModalContext();
  const { state, paymentMethodId } = useFsuiContext();

  const prepareSubmissionData = useCallback(() => {
    const hasNonDefaultTermsAccepted = walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.NONDEFAULT_TERMS];
    const nonDefaultTermsAccepted = hasNonDefaultTermsAccepted ? Object.values(nonDefaultTerms).flatMap((terms) => terms) : [];
    const nonDefaultResourceRequested = hasNonDefaultTermsAccepted ? Object.keys(nonDefaultTerms) : [];

    const allLabelProviderIds = [
      ...(walletForm?.[FORM_REQUIREMENTS.RESOURCES]?.[FORM_REQUIREMENTS.LABEL_PROVIDER_IDS] || []),
      ...nonDefaultResourceRequested,
    ];

    const submissionData = clonedeep(walletForm);

    if (shouldShowAdyenDropInComponent && state?.auctanePaySessionId) {
      submissionData[FORM_REQUIREMENTS.METADATA].additionalMetadata = submissionData[FORM_REQUIREMENTS.METADATA].additionalMetadata ?? {};
      if (paymentMethodId) {
        submissionData[FORM_REQUIREMENTS.METADATA].additionalMetadata.paymentMethodId = paymentMethodId;
      } else {
        submissionData[FORM_REQUIREMENTS.METADATA].additionalMetadata.sessionId = state.auctanePaySessionId;
      }
    }
    if (nonDefaultTermsAccepted.length) {
      submissionData[FORM_REQUIREMENTS.METADATA][FORM_REQUIREMENTS.ACCEPTED_TERMS].push(...nonDefaultTermsAccepted);
    }
    if (nonDefaultResourceRequested.length) {
      submissionData[FORM_REQUIREMENTS.RESOURCES][FORM_REQUIREMENTS.LABEL_PROVIDER_IDS] = allLabelProviderIds;
    }

    return { submissionData, allLabelProviderIds, nonDefaultResourceRequested };
  }, [walletForm, nonDefaultTerms, shouldShowAdyenDropInComponent, state?.auctanePaySessionId, paymentMethodId]);

  return { prepareSubmissionData };
};
